// the deals-board presentation vocabulary (kept in a non-component module so DealsBoard.tsx stays
// component-only for fast-refresh). Presentation labels ONLY — no server enum leak (like offerStatusLabel). The
// real vocab lives in Postgres CHECK constraints: status ∈ open|confirmed|cancelled|completed;
// type ∈ rehearsal|content|write|pr|other. Unknown values are title-cased, never dropped.

/** Human column label for a raw opportunity status, or a booking show status (offer|hold|confirmed). */
export function dealStatusLabel(status: string): string {
  switch (status) {
    // The BOOKING pipeline's stages (offer / hold / confirmed) need no arms of their own — the default
    // title-cases them to exactly the right words. Only a value whose display spelling DIFFERS from its
    // stored one earns a case here, which is why `canceled` has one and `offer` does not.
    case "open":
      return "Open";
    case "confirmed":
      return "Confirmed";
    case "completed":
      return "Completed";
    case "ready":
      return "Ready to confirm";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Other";
  }
}

/**
 * The settlement worksheet's three words, from the raw `pending|in_progress|settled`.
 *
 * The unknown fallback title-cases rather than naming a specific state: this is a MONEY surface, and a future
 * status ("disputed", "void") rendered confidently as "Pending" is a wrong answer where the raw value would at
 * least be an honest one. Same discipline as `dealStatusLabel` above.
 */
export function settlementStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "settled":
      return "Settled";
    case "in_progress":
      return "In progress";
    case "pending":
      return "Pending";
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ") : "—";
  }
}

/** Human label for the 5 opportunity types. Unknown → title-cased, never dropped.*/
export function dealTypeLabel(type: string | null | undefined): string {
  switch (type) {
    case "rehearsal":
      return "Rehearsal";
    case "content":
      return "Content";
    case "write":
      return "Write";
    case "pr":
      return "PR";
    case "other":
      return "Other";
    default:
      return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Deal";
  }
}
